// Copyright (c) 2024 Readymadeui
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {localizeKey, localizeArt, localizeGenre, localizeGenres} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import Collapsible from './Collapsible';
import LinkInput from './LinkInput';
import TextUserPair from './TextUserPair';
import CopyTextButton from './CopyTextButton';
import PageHeader from './PageHeader';
import { useMediaQuery } from '@mantine/hooks';
import DotSpinner from './DotSpinner';
import Tabs from './Tabs';
import ArtPage from './ArtPage';
import getIconForLink from '../iconForLink.js';
import { Link } from 'react-router-dom';
import PlatformRatings from './PlatformRatings';
import ArtTabs from './ArtTabs';
import { DIMENSION_COLORS, tagSearchPath } from './ArtTags.js';
import TagPicker from './TagPicker';
import 'delayed-scroll-restoration-polyfill';
import ArtThumbnail from './ArtThumbnail.js';
import { SegmentedControl, TextInput, ActionIcon, Paper, Text, Tooltip, Avatar, Modal, Button, Stack, Badge } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { formatYear } from './../utils.js'; // Import the global function

// Move cache outside the component to persist across renders
const cache = new Map();

const TRENDING_RATING_ICONS = {
  imdb: { src: '/imdb_small_icon.png' },
  kinopoisk: { src: '/kinopoisk_small_icon.png' },
  // Apple Books deliberately omitted: their rating is unreliable so we
  // don't surface it on the hover overlay (or anywhere else — see the
  // matching branch in PlatformRatings.js).
  steam: { src: '/steam_icon.png' },
  rottentomatoes: { src: '/tomato_icon.png' },
  reddit: { src: '/reddit_icon.png' },
  amazon: { src: '/amazon_icon.png' },
  goodreads: { src: '/goodreads_icon.png' },
  metacritic: { src: '/metacritic_icon.png' },
  opencritic: { src: '/opencritic_icon.png' },
  litres: { src: '/litres_icon.png' },
  livelib: { src: '/livelib_icon.png' },
};

// packRowsByAspect reorders `items` so consecutive groups of `cols` share
// a thumbnail-shape bucket. Two buckets only:
//   * "landscape" — height < width
//   * "portrait"  — height >= width (includes square)
// Sort order is preserved as much as possible: each row starts with the
// earliest-remaining item and its bucket becomes the row target; matching
// items land in sort order, non-matches get deferred to later rows.
// Items without dimensions count as wildcards; if >50% of the list is
// missing dimensions we skip the reorder entirely.
const packRowsByAspect = (items, cols) => {
  if (!items || items.length <= cols) return items;
  const bucketOf = (it) => {
    const w = it && it.thumbnailWidth;
    const h = it && it.thumbnailHeight;
    if (!(w > 0 && h > 0)) return null;
    return h < w ? 'landscape' : 'portrait';
  };
  const known = items.filter((it) => bucketOf(it) !== null).length;
  if (known < items.length * 0.5) return items;

  const result = [];
  const remaining = items.slice();
  while (remaining.length > 0) {
    const first = remaining.shift();
    const row = [first];
    const target = bucketOf(first);
    const matches = (b) => b === null || target === null || b === target;
    let i = 0;
    while (row.length < cols && i < remaining.length) {
      if (matches(bucketOf(remaining[i]))) {
        row.push(remaining[i]);
        remaining.splice(i, 1);
      } else {
        i++;
      }
    }
    result.push(...row);
  }
  return result;
};

const TrendingRatingsOverlay = ({ art }) => {
  // Skip on touch-only devices: tap opens the detail view which already shows ratings,
  // and :hover gets stuck on tap so the overlay would linger over the tapped thumbnail.
  if (typeof window !== 'undefined' && !window.matchMedia('(hover: hover)').matches) {
    return null;
  }
  const infos = art.otherPlatformInfos || [];
  const rtAudience = infos.find(r => r?.name === 'rottenTomatoesAudience');
  // Show any platform we have linkage for, even if the score itself hasn't
  // arrived yet — that way an art tied to Metacritic/RT is obvious on hover
  // instead of silently disappearing when the scrape is still pending.
  const ratings = infos.filter(r => r && TRENDING_RATING_ICONS[r.name?.toLowerCase()]);
  if (ratings.length === 0) return null;
  return (
    <div
      className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none flex flex-col items-center justify-center gap-1.5 p-2"
      style={{ background: 'rgba(0,0,0,0.72)' }}
    >
      {ratings.map((r) => {
        const icon = TRENDING_RATING_ICONS[r.name.toLowerCase()];
        const isRT = r.name.toLowerCase() === 'rottentomatoes';
        // RT scores are integer percentages — render without a decimal
        // and append the audience score when we have it. Any missing
        // half becomes '-' so linkage is visible even without data.
        const critStr = r.score ? (isRT ? `${Math.round(r.score)}%` : r.score.toFixed(1)) : '-';
        let scoreText;
        if (isRT) {
          const audStr = rtAudience?.score ? `${Math.round(rtAudience.score)}%` : '-';
          scoreText = rtAudience ? `${critStr} / ${audStr}` : critStr;
        } else {
          scoreText = critStr;
        }
        return (
          <div key={r.name} className="flex items-center gap-1.5 leading-none">
            {icon.text ? (
              <span className="text-base font-bold" style={{ color: '#FF3131' }}>{icon.text}</span>
            ) : (
              <img
                src={icon.src}
                alt=""
                className={`w-6 h-6 rounded ${icon.padded ? 'bg-white p-0.5' : ''}`}
              />
            )}
            <span className="text-base font-semibold text-white">{scoreText}</span>
          </div>
        );
      })}
    </div>
  );
};

const AddEvent = ({repeatedEnter = 0}) => {
  const isDesktop = useMediaQuery('(min-width: 48em)');
  const myUser = useContext(MyUserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [art, setArt] = useState(null);
  const [addedEvents, setAddedEvents] = useState([]);
  // const [successSign, setSuccessSign] = useState(null); // like, todo, finished

  const [searching, setSearching] = useState(false);
  //const [foundArts, setFoundArts] = useState([]);

  // const [foundArtsByType, setFoundArtsByType] = useState(new Map());

  const [foundMovies, setFoundMovies] = useState(null);
  const [foundBooks, setFoundBooks] = useState(null);
  const [foundGames, setFoundGames] = useState(null);
  const [foundUsers, setFoundUsers] = useState(null);
  const [userOnlySearch, setUserOnlySearch] = useState(false);
  // Tag search: { tags: [{dimension, value, ru}], family } from repeated ?tag=dim:value and &family=1
  const [tagSearch, setTagSearch] = useState(null);

  const userSearchSeqRef = useRef(0);
  const userSuggestSeqRef = useRef(0);
  const [userSuggestions, setUserSuggestions] = useState(null); // top-N overlay shown when input starts with @

  const searchInputRef = useRef(null);
  const [renderCount, setRenderCount] = useState(0);
  const prevRepeatedEnterRef = useRef(repeatedEnter);
  useEffect(() => {
    if (repeatedEnter !== prevRepeatedEnterRef.current) {
      prevRepeatedEnterRef.current = repeatedEnter;
      if (searchInputRef.current) {
        // Mark this focus as user-initiated so the input's onFocus
        // shows the search-history dropdown — same as clicking the
        // input directly. Without this, focus arrives without a
        // preceding pointer/touch event and the ref stays false.
        userInitiatedFocusRef.current = true;
        searchInputRef.current.focus();
      }
    }
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const searchTerm = queryParams.get('term');
    const limit = queryParams.get('limit') || 20;
    const tagParams = queryParams.getAll('tag').filter(t => t.includes(':'));
    if (tagParams.length > 0) {
      const tags = tagParams.map(t => {
        const i = t.indexOf(':');
        return { dimension: t.slice(0, i), value: t.slice(i + 1), ru: '' };
      });
      runTagSearch({ tags, family: queryParams.get('family') !== '0' });
      return;
    }
    // const author = queryParams.get('author');
    if (searchTerm) {
    // if (searchTerm || author) {
      // if (author) {
      //   searchTerm = "author:"+author;
      // }
      if (searchTerm.startsWith('@')) {
        setUserOnlySearch(true);
        handleSearchUsers(searchTerm.slice(1));
      } else {
        runSerializedSearch(searchTerm, limit);
        handleSearchUsers(searchTerm);
      }
      searchInputRef.current.value = searchTerm;
    }
  }, []);

  useEffect(() => {
    // Enable scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  const [enterPressed, setEnterPressed] = useState(false); // to prevent suggest flickering when response for old request comes

  const fetchUserSuggestions = (prefix, limit = 3) => {
    const mySeq = ++userSuggestSeqRef.current;
    if (!prefix) {
      setUserSuggestions([]);
      return;
    }
    axiosInstance
      .get(`/searchUsers?userName=${encodeURIComponent(prefix)}&limit=${limit}&allUsers=true`)
      .then((response) => {
        if (mySeq !== userSuggestSeqRef.current) return;
        setUserSuggestions(response.data || []);
      })
      .catch((error) => {
        if (mySeq !== userSuggestSeqRef.current) return;
        console.log(error);
        setUserSuggestions([]);
      });
  };

  const handleSearchUsers = (term, limit = 50) => {
    if (term === "") {
      return;
    }
    setFoundUsers(null);
    const mySeq = ++userSearchSeqRef.current;
    axiosInstance
      .get(`/searchUsers?userName=${encodeURIComponent(term)}&limit=${limit}&allUsers=true`)
      .then((response) => {
        if (mySeq !== userSearchSeqRef.current) return;
        setFoundUsers(response.data || []);
      })
      .catch((error) => {
        if (mySeq !== userSearchSeqRef.current) return;
        console.log(error);
        setFoundUsers([]);
      });
  };

  const typingSearchClearTimeoutRef = useRef(null);
  const [searchSuggest, setSearchSuggest] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const userInitiatedFocusRef = useRef(false);
  const swipeTouchXRef = useRef(0);
  const swipeTouchYRef = useRef(0);
  const swipeActiveRef = useRef(false);
  const handleSearchSuggest = (term) => {
    axiosInstance
    .get(`/arts/searchSuggest?term=${encodeURIComponent(term)}`)
    .then((response) => {
      // console.log("term: " + term + ", searchInputRef.current.value: " + searchInputRef.current.value);
      if (searchInputRef.current.value !== term) { // late response // TODO: move into the component's main code
        return;
      }
      setSearchSuggest(response.data);
      setHighlightedIndex(-1);
    })
    .catch((error) => {
      console.log(error);
    })
  };
  const handleSearchHistory = () => {
    axiosInstance
    .get(`/arts/searchHistory`)
    .then((response) => {
      setSearchHistory(response.data || []);
    })
    .catch((error) => {
      console.log(error);
    })
  };

  const handleSearchArt = (term, artType, limit=25) => {
    if (term === "") {
      return Promise.resolve();
    }

    const cacheKey = `${term}-${artType}`;
    const cachedResult = cache.get(cacheKey);

    // Remove stale data from cache
    for (const [key, value] of cache.entries()) {
      if (Date.now() - value.timestamp >= 10 * 60 * 1000) { // 10 minutes
        cache.delete(key);
      }
    }

    if (cachedResult && Date.now() - cachedResult.timestamp < 10 * 60 * 1000) {
      const data = cachedResult.data;
      if (artType === "movie") {
        setFoundMovies(data);
      } else if (artType === "book") {
        setFoundBooks(data);
      } else {
        setFoundGames(data);
      }
      return Promise.resolve();
    }

    setSearching(true);
    setArt(null);


    return axiosInstance
      .get(`/arts/search?term=${encodeURIComponent(term)}&artType=${artType}&limit=${limit}`)
      .then((response) => {
        setSearching(false);
        setSearchSuggest(null);

        if (addedEvents.length > 0) {
          return;
        }

        const data = response.data;
        cache.set(cacheKey, { data, timestamp: Date.now() });

        if (artType === "movie") {
          setFoundMovies(data);
        } else if (artType === "book") {
          setFoundBooks(data);
        } else {
          setFoundGames(data);
        }
      })
      .catch((error) => {
        console.error(error);
        setSearching(false);
        if (error.response.status === 400) {
          alert(error.response.data);
        }
      });
  };

  // Serialize the three type-scoped searches so movies land first,
  // then games, then books — the perceived response feels snappier
  // than blocking on all three in parallel because the most-common
  // hit (movies) paints without waiting for the slower book search.
  const runSerializedSearch = async (term, limit) => {
    setFoundMovies(null);
    setFoundGames(null);
    setFoundBooks(null);
    await handleSearchArt(term, "movie", limit);
    await handleSearchArt(term, "videoGame", limit);
    await handleSearchArt(term, "book", limit);
  };

  // Tag search: local catalog only, three per-type requests like text search.
  const fetchArtsByTag = (ts, artType) => {
    const p = new URLSearchParams();
    ts.tags.forEach(t => p.append('tag', `${t.dimension}:${t.value}`));
    p.set('family', ts.family ? '1' : '0');
    p.set('artType', artType);
    p.set('limit', '60');
    return axiosInstance.get(`/arts/byTag?${p.toString()}`)
      .then((response) => {
        if (artType === "movie") setFoundMovies(response.data);
        else if (artType === "book") setFoundBooks(response.data);
        else setFoundGames(response.data);
      })
      .catch((error) => { console.error(error); });
  };

  const runTagSearch = async (ts) => {
    setTagSearch(ts);
    setUserOnlySearch(false);
    setFoundUsers([]);
    setFoundMovies(null);
    setFoundGames(null);
    setFoundBooks(null);
    setArt(null);
    setSearching(true);
    if (ts.tags.some(t => !t.ru)) {
      // Russian wording comes from the vocabulary, not the URL.
      const p = new URLSearchParams();
      ts.tags.forEach(t => p.append('tag', `${t.dimension}:${t.value}`));
      axiosInstance.get(`/tags/lookup?${p.toString()}`)
        .then((r) => setTagSearch(prev => prev && prev.tags.length === r.data.length
          ? { ...prev, tags: prev.tags.map((t, i) => ({ ...t, ru: r.data[i].ru || '' })) } : prev))
        .catch((e) => console.error(e));
    }
    await fetchArtsByTag(ts, "movie");
    await fetchArtsByTag(ts, "videoGame");
    await fetchArtsByTag(ts, "book");
    setSearching(false);
  };

  const updateTagSearch = (ts) => {
    if (ts.tags.length === 0) {
      setTagSearch(null);
      setFoundMovies(null); setFoundBooks(null); setFoundGames(null); setFoundUsers(null);
      navigate('/addEvent', { replace: true });
      return;
    }
    navigate(tagSearchPath(ts.tags, ts.family), { replace: true });
    runTagSearch(ts);
  };

  const isMobileDeviceExceptAndroid = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iphone|ipad|ipod|windows phone|blackberry/i.test(userAgent);
  };

  const isFirefox = () => {
    return navigator.userAgent.toLowerCase().includes('firefox');
  };

  const handleArtSubmit = (link) => {
    axiosInstance
    .post(`/arts`, { wikiArticleLink: link })
    .then((response) => {
      // setAddedEvents(prevObjects => [response.data, ...prevObjects]); // not sure
      // setFoundMovies(null);
      // setFoundGames(null);
      // setFoundBooks(null);
      // setActiveTab('movie');
      //setArt(response.data);
      navigate("/arts/"+response.data.id);
      // setLinkInput(""); // not sure
      // setSuccessSign(eventType);
    })
    .catch((error) => {
      console.error(error);
      // setSuccessSign(null);
      // setLinkInputErr(true);
    });
  };

  // Determine the tab to show based on found results.
  // Expects non-null results and exactly one tab with results.
  const determineTabBasedOnResults = () => {
    let res;
    if (foundMovies.length > 0) {
      res = 'movie';
    } else if (foundBooks.length > 0) {
      res = 'book';
    } else if (foundGames.length > 0) {
      res = 'videoGame';
    } else if (foundUsers && foundUsers.length > 0) {
      res = 'users';
    }
    return res;
  };

  const getActiveTabFromURL = () => {
    const p = new URLSearchParams(location.search).get('tab');
    return (p === 'book' || p === 'videoGame' || p === 'users') ? p : 'movie';
  };
  const [activeTab, setActiveTabState] = useState(getActiveTabFromURL);
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`/addEvent?${params.toString()}`, { replace: true });
  };

  // Update activeTab dynamically when results are fetched
  useEffect(() => {
    if (!foundMovies || !foundBooks || !foundGames || !foundUsers) { // too early to determine tab
      return;
    }
    let nonZeroTabs = 0;
    if (foundMovies.length > 0) nonZeroTabs++;
    if (foundBooks.length > 0) nonZeroTabs++;
    if (foundGames.length > 0) nonZeroTabs++;
    if (foundUsers.length > 0) nonZeroTabs++;
    if (nonZeroTabs == 1) {
      setActiveTab(determineTabBasedOnResults());
    }
  }, [foundMovies, foundBooks, foundGames, foundUsers]);

  const foundArtContainerByTab = (tab) => {
    if (tab === "movie") {
      return foundMovies;
    } else if (tab === "book") {
      return foundBooks;
    } else if (tab === "videoGame") {
      return foundGames;
    } else if (tab === "users") {
      return null; // rendered separately
    } else {
      alert("Unexpected tab");
      return foundMovies;
    }
  }

  const eventTypeStr = (i) => {
    const m = {
      0: "none",
      1: "finished",
      2: "todo",
      3: "like"
    };
    return m[i];
  }

  const getTrendingCategoryFromLocation = () => {
    const p = new URLSearchParams(location.search).get('cat');
    return (p === 'book' || p === 'videoGame') ? p : 'movie';
  };
  const trendingCategory = getTrendingCategoryFromLocation();
  const trendingCategoryRef = useRef(trendingCategory);
  trendingCategoryRef.current = trendingCategory;

  const setTrendingCategory = (cat) => {
    trendingCategoryRef.current = cat;
    const params = new URLSearchParams(location.search);
    params.set('cat', cat);
    navigate(`/addEvent?${params.toString()}`, { replace: true });
  };

  // const [showTrending, setShowTrending] = useState(true); // if false, recommendations are shown
  const [trendingMovies, setTrendingMovies] = useState(null);
  const [trendingBooks, setTrendingBooks] = useState(null);
  const [trendingGames, setTrendingGames] = useState(null);
  const [trendingMoviesUpdatedTime, setTrendingMoviesUpdatedTime] = useState(null);
  const [trendingBooksUpdatedTime, setTrendingBooksUpdatedTime] = useState(null);
  const [trendingGamesUpdatedTime, setTrendingGamesUpdatedTime] = useState(null);
  // Set of art IDs that the backend flags as "just published" for the
  // current trending pull (populated when ?popMostRecent=true or when the
  // user hasn't seen the latest hots notification yet). Used to render a
  // "New" badge on top of the corresponding thumbnails.
  const [trendingMoviesMostRecentIDs, setTrendingMoviesMostRecentIDs] = useState(null);
  const [trendingBooksMostRecentIDs, setTrendingBooksMostRecentIDs] = useState(null);
  const [trendingGamesMostRecentIDs, setTrendingGamesMostRecentIDs] = useState(null);
  const [selectedTrendingId, setSelectedTrendingId] = useState(null);
  const [trendingDetails, setTrendingDetails] = useState({});
  const [selectedGenre, setSelectedGenre] = useState(null);

  const currentTrending =
    trendingCategory === 'book' ? trendingBooks :
    trendingCategory === 'videoGame' ? trendingGames :
    trendingMovies;
  const currentTrendingUpdatedTime =
    trendingCategory === 'book' ? trendingBooksUpdatedTime :
    trendingCategory === 'videoGame' ? trendingGamesUpdatedTime :
    trendingMoviesUpdatedTime;
  const currentTrendingMostRecentIDs =
    trendingCategory === 'book' ? trendingBooksMostRecentIDs :
    trendingCategory === 'videoGame' ? trendingGamesMostRecentIDs :
    trendingMoviesMostRecentIDs;

  useEffect(() => {
    setSelectedGenre(null);
  }, [trendingCategory]);

  const prevResetRef = useRef(repeatedEnter);
  useEffect(() => {
    if (repeatedEnter !== prevResetRef.current) {
      prevResetRef.current = repeatedEnter;
      setFoundMovies(null);
      setFoundBooks(null);
      setFoundGames(null);
      setFoundUsers(null);
      setUserOnlySearch(false);
      setTagSearch(null);
      setSearchSuggest(null);
      setSelectedTrendingId(null);
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
      if (location.search) {
        navigate('/addEvent', { replace: true });
      }
    }
  }, [repeatedEnter]);

  const scrollDetailIntoView = (el) => {
    if (!el) return;
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom > window.innerHeight) {
        window.scrollBy({ top: rect.bottom - window.innerHeight + 60, behavior: 'smooth' });
      }
    });
  };

  // Batch-fetch descriptions when a trending list loads.
  // The backend caps each request at 100 ids ("ids: 1-100 required"), so chunk.
  const fetchDescriptionsFor = (arts) => {
    if (!arts || arts.length === 0) return;
    const ids = arts.map(a => a.id).filter(id => !trendingDetails[id]);
    if (ids.length === 0) return;
    const chunks = [];
    for (let i = 0; i < ids.length; i += 100) chunks.push(ids.slice(i, i + 100));
    Promise.all(chunks.map(chunk => axiosInstance.post('/arts/descriptions', { ids: chunk })))
      .then((responses) => {
        const merged = responses.reduce((acc, r) => Object.assign(acc, r.data), {});
        setTrendingDetails(prev => ({ ...prev, ...merged }));
      })
      .catch((error) => console.error('Failed to fetch art descriptions:', error));
  };
  useEffect(() => { fetchDescriptionsFor(trendingMovies); }, [trendingMovies]);
  useEffect(() => { fetchDescriptionsFor(trendingBooks); }, [trendingBooks]);
  useEffect(() => { fetchDescriptionsFor(trendingGames); }, [trendingGames]);

  const handleSelectTrending = (artId) => {
    setSelectedTrendingId(selectedTrendingId === artId ? null : artId);
  };
  const fetchTrendingEndpoint = (endpoint, setter, setterTime, setterMostRecent) => {
    const queryParams = new URLSearchParams(location.search);
    const popMostRecent = queryParams.get('popMostRecent');
    return axiosInstance
      .get(`${endpoint}${popMostRecent ? `?popMostRecent=${popMostRecent}` : ''}`)
      .then((response) => {
        setter(response.data.arts || []);
        setterTime(response.data.updatedTime);
        setterMostRecent(new Set(response.data.mostRecentArtIds || []));
      })
      .catch((error) => {
        // 404 = "no hot arts" — treat as empty list so the UI can render
        // "Coming soon" instead of hanging on the loading state.
        if (error.response && error.response.status === 404) {
          setter([]);
          setterMostRecent(new Set());
        } else {
          console.log(error);
          setter([]);
          setterMostRecent(new Set());
        }
      });
  };

  useEffect(() => {
    // Kick off all three category fetches in parallel. Each renders as soon
    // as its own response lands; a slow books/games fetch doesn't block the
    // movies grid, and vice versa.
    fetchTrendingEndpoint('/trendingMovies', setTrendingMovies, setTrendingMoviesUpdatedTime, setTrendingMoviesMostRecentIDs);
    fetchTrendingEndpoint('/trendingBooks', setTrendingBooks, setTrendingBooksUpdatedTime, setTrendingBooksMostRecentIDs);
    fetchTrendingEndpoint('/trendingGames', setTrendingGames, setTrendingGamesUpdatedTime, setTrendingGamesMostRecentIDs);
    handleSearchHistory();
  }, []);

  const [notFoundModalOpen, setNotFoundModalOpen] = useState(false);
  const [notFoundLink, setNotFoundLink] = useState('');
  const [notFoundNameAndYear, setNotFoundNameAndYear] = useState('');
  const [notFoundCategory, setNotFoundCategory] = useState('movie');
  const [notFoundSubmitting, setNotFoundSubmitting] = useState(false);
  const [notFoundSubmitted, setNotFoundSubmitted] = useState(false);

  const submitNotFoundHint = () => {
    const term = (searchTerm || (searchInputRef.current && searchInputRef.current.value) || '').trim();
    if (!term) return;
    setNotFoundSubmitting(true);
    axiosInstance
      .post('/searchNotFoundHints', {
        searchTerm: term,
        linkToArt: notFoundLink.trim(),
        nameAndYear: notFoundNameAndYear.trim(),
        category: notFoundCategory,
      })
      .then(() => {
        setNotFoundSubmitted(true);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setNotFoundSubmitting(false);
      });
  };

  const openNotFoundModal = () => {
    setNotFoundLink('');
    setNotFoundNameAndYear('');
    setNotFoundCategory(activeTab === 'users' ? 'movie' : activeTab);
    setNotFoundSubmitted(false);
    setNotFoundModalOpen(true);
  };
  const closeNotFoundModal = () => {
    setNotFoundModalOpen(false);
  };

  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const showUserSuggestList = !enterPressed && typeof searchTerm === 'string' && searchTerm.startsWith('@') && userSuggestions && userSuggestions.length > 0;
  const showSuggestList = !showUserSuggestList && !enterPressed && searchSuggest && searchSuggest.length > 0;
  const showHistoryList = !showUserSuggestList && showSearchHistory && (!searchTerm || searchTerm.length === 0) && searchHistory && searchHistory.length > 0;
  const categoryLabels = {
    movie: language === "ru" ? "Фильмы" : "Movies",
    book: language === "ru" ? "Книги" : "Books",
    videoGame: language === "ru" ? "Игры" : "Video Games"
  };
  const categoryOrder = ["movie", "book", "videoGame"];

  const formatUpdatedAgo = (updatedTime) => {
    if (!updatedTime) {
      return "";
    }
    const now = Date.now();
    const updatedAt = new Date(updatedTime).getTime();
    let diffMinutes = Math.max(0, Math.floor((now - updatedAt) / 60000));
    if (diffMinutes < 60) {
      const unit = language === "ru" ? "мин" : (diffMinutes === 1 ? "minute" : "minutes");
      return language === "ru"
        ? `Обновлено ${diffMinutes} ${unit} назад`
        : `Updated ${diffMinutes} ${unit} ago`;
    }
    const diffHoursTotal = Math.floor(diffMinutes / 60);
    const ruPluralHour = (count) => {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) {
        return "час";
      }
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return "часа";
      }
      return "часов";
    };
    if (diffHoursTotal < 24) {
      const unit = language === "ru" ? ruPluralHour(diffHoursTotal) : (diffHoursTotal === 1 ? "hour" : "hours");
      return language === "ru"
        ? `Обновлено ${diffHoursTotal} ${unit} назад`
        : `Updated ${diffHoursTotal} ${unit} ago`;
    }
    const diffDays = Math.floor(diffHoursTotal / 24);
    const diffHoursRemainder = diffHoursTotal % 24;
    const dayUnit = language === "ru" ? "дн" : (diffDays === 1 ? "day" : "days");
    const hourUnit = language === "ru" ? ruPluralHour(diffHoursRemainder) : (diffHoursRemainder === 1 ? "hour" : "hours");
    if (diffHoursRemainder === 0) {
      return language === "ru"
        ? `Обновлено ${diffDays} ${dayUnit} назад`
        : `Updated ${diffDays} ${dayUnit} ago`;
    }
    return language === "ru"
      ? `Обновлено ${diffDays} ${dayUnit} ${diffHoursRemainder} ${hourUnit} назад`
      : `Updated ${diffDays} ${dayUnit} ${diffHoursRemainder} ${hourUnit} ago`;
  };

  const performSearch = (term) => {
    const nextTerm = term.trim();
    if (nextTerm === "") {
      return;
    }

    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
    setEnterPressed(true);
    setSearchSuggest(null);
    setShowSearchHistory(false);
    setSearchTerm(nextTerm);
    setTagSearch(null);
    if (searchInputRef.current) {
      searchInputRef.current.value = nextTerm;
    }

    if (nextTerm.startsWith("https://")) {
      handleArtSubmit(nextTerm);
      return;
    }

    if (nextTerm.startsWith("@")) {
      setUserOnlySearch(true);
      setFoundMovies(null);
      setFoundBooks(null);
      setFoundGames(null);
      handleSearchUsers(nextTerm.slice(1));
      setActiveTab('users');
      navigate(`/addEvent?term=${encodeURIComponent(nextTerm)}&tab=users`, { replace: true });
      return;
    }

    setUserOnlySearch(false);
    runSerializedSearch(nextTerm);
    handleSearchUsers(nextTerm);

    navigate(`/addEvent?term=${encodeURIComponent(nextTerm)}&tab=${activeTab}`, { replace: true });
  };

  return (
    <div className="w-full px-0 md:px-1 z-1">
      <div>
        {(() => {
        const searchInput = (
          <TextInput
            ref={searchInputRef}
            size={isDesktop ? "md" : "sm"}
            radius="xl"
            style={{ width: '100%' }}
            placeholder={language === "ru" ? "Поиск по фильмам, книгам и играм..." : "Search movies, books, or games..."}
            value={searchTerm}
            onKeyDown={(e) => {
              const items = showUserSuggestList ? userSuggestions : (showSuggestList ? searchSuggest : (showHistoryList ? searchHistory : null));
              const itemCount = items ? items.length : 0;

              if (e.key === 'ArrowDown' && itemCount > 0) {
                e.preventDefault();
                setHighlightedIndex(prev => prev < itemCount - 1 ? prev + 1 : 0);
                return;
              }
              if (e.key === 'ArrowUp' && itemCount > 0) {
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : itemCount - 1);
                return;
              }
              if (e.key === 'Escape') {
                setSearchSuggest(null);
                setUserSuggestions(null);
                setShowSearchHistory(false);
                setHighlightedIndex(-1);
                return;
              }
              if (e.key === 'Enter') {
                if (showUserSuggestList && highlightedIndex >= 0 && items && items[highlightedIndex]) {
                  navigate(`/users/${items[highlightedIndex].id}/likes`);
                  setHighlightedIndex(-1);
                  return;
                }
                if (highlightedIndex >= 0 && items && items[highlightedIndex]) {
                  const selected = showSuggestList ? items[highlightedIndex].text : items[highlightedIndex];
                  performSearch(selected);
                } else {
                  performSearch(e.target.value);
                }
                setHighlightedIndex(-1);
                return;
              }
              setSearchSuggest(null);
              setShowSearchHistory(false);
              setHighlightedIndex(-1);
            }}
            onChange={(e) => {
              const value = e.target.value;
              setEnterPressed(false);
              setShowSearchHistory(false);
              setSearchTerm(value);
              clearTimeout(typingSearchClearTimeoutRef.current);
              if (value.startsWith('@')) {
                setSearchSuggest(null);
                typingSearchClearTimeoutRef.current = setTimeout(() => {
                  fetchUserSuggestions(value.slice(1));
                }, 90);
              } else {
                setUserSuggestions(null);
                typingSearchClearTimeoutRef.current = setTimeout(() => {
                  handleSearchSuggest(value);
                }, 90);
              }
            }}
            onMouseDown={() => { userInitiatedFocusRef.current = true; }}
            onTouchStart={() => { userInitiatedFocusRef.current = true; }}
            onPointerDown={() => { userInitiatedFocusRef.current = true; }}
            onFocus={() => {
              if (userInitiatedFocusRef.current) {
                setShowSearchHistory(true);
              }
              userInitiatedFocusRef.current = false;
            }}
            onBlur={() => {
              setTimeout(() => { setShowSearchHistory(false); }, 150);
            }}
            leftSection={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192.904 192.904" width="16px" style={{ fill: "var(--mantine-color-dimmed)" }}>
                <path d="m190.707 180.101-47.078-47.077c11.702-14.072 18.752-32.142 18.752-51.831C162.381 36.423 125.959 0 81.191 0 36.422 0 0 36.423 0 81.193c0 44.767 36.422 81.187 81.191 81.187 19.688 0 37.759-7.049 51.831-18.751l47.079 47.078a7.474 7.474 0 0 0 5.303 2.197 7.498 7.498 0 0 0 5.303-12.803zM15 81.193C15 44.694 44.693 15 81.191 15c36.497 0 66.189 29.694 66.189 66.193 0 36.496-29.692 66.187-66.189 66.187C44.693 147.38 15 117.689 15 81.193z" />
              </svg>
            }
            rightSection={
              (isMobileDeviceExceptAndroid() || isFirefox()) && searchTerm ? (
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => {
                  setSearchTerm('');
                  searchInputRef.current.value = '';
                  searchInputRef.current.focus();
                }}>
                  &times;
                </ActionIcon>
              ) : null
            }
          />
        );
        return isDesktop
          ? <div className="max-w-xl mx-auto px-3">{searchInput}</div>
          : <PageHeader title={searchInput} />;
        })()}
        <div className="flex justify-center">
          <div className="absolute z-50 rounded-md max-w-md mx-auto w-full" style={{ background: "var(--mantine-color-body)", border: (showSuggestList || showHistoryList || showUserSuggestList) ? "1px solid var(--mantine-color-default-border)" : "none" }}>
            {showUserSuggestList && userSuggestions.map((u, index) => (
              <div
                key={u.id}
                className={"p-2 cursor-pointer flex items-center gap-2" + (index===0 ? " rounded-t-md" : "") + (index === userSuggestions.length-1 ? " rounded-b-md" : "")}
                style={{ background: index === highlightedIndex ? "var(--mantine-color-default-hover)" : "var(--mantine-color-body)" }}
                onMouseOver={() => setHighlightedIndex(index)}
                onMouseOut={() => setHighlightedIndex(-1)}
                onMouseDown={(e) => { e.preventDefault(); }}
                onClick={() => {
                  navigate(`/users/${u.id}/likes`);
                }}
              >
                <Avatar src={u.avatarUrl} size="sm" radius="xl" />
                <span>{u.userName}</span>
              </div>
            ))}
            {showSuggestList && searchSuggest.map((suggest, index) => (
              <div
                key={suggest.imageUrl}
                className={"p-2 cursor-pointer flex items-center" + (index===0 ? " rounded-t-md" : "") + (index === searchSuggest.length-1 ? " rounded-b-md" : "")}
                style={{ background: index === highlightedIndex ? "var(--mantine-color-default-hover)" : "var(--mantine-color-body)" }}
                onMouseOver={() => setHighlightedIndex(index)}
                onMouseOut={() => setHighlightedIndex(-1)}
                onClick={() => {
                  performSearch(suggest.text);
                }}
              >
                {suggest.text}
              </div>
            ))}
            {showHistoryList && searchHistory.map((term, index) => (
              <div
                key={`${term}-${index}`}
                className={"p-2 cursor-pointer flex items-center" + (index===0 ? " rounded-t-md" : "") + (index === searchHistory.length-1 ? " rounded-b-md" : "")}
                style={{ background: index === highlightedIndex ? "var(--mantine-color-default-hover)" : "var(--mantine-color-body)" }}
                onMouseOver={() => setHighlightedIndex(index)}
                onMouseOut={() => setHighlightedIndex(-1)}
                onClick={() => {
                  performSearch(term);
                }}
              >
                {term}
              </div>
            ))}
          </div>
        </div>
      </div>

      {
      (foundMovies || foundBooks || foundGames || foundUsers) &&
      <div className="my-2 sm:relative">
        <div className="flex justify-center">
          <ArtTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            language={language}
            movies={userOnlySearch ? undefined : foundMovies}
            books={userOnlySearch ? undefined : foundBooks}
            games={userOnlySearch ? undefined : foundGames}
            users={foundUsers}
          />
        </div>
        <div className="flex justify-center my-2 sm:my-0 sm:absolute sm:right-2 sm:top-1/2 sm:-translate-y-1/2 sm:block">
          <Text
            size="xs"
            c="dimmed"
            onClick={openNotFoundModal}
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              maxWidth: 220,
              lineHeight: 1.2,
            }}
          >
            {language === 'ru'
              ? 'Не нашли то, что искали?'
              : "Didn't find what you were looking for?"}
          </Text>
        </div>
      </div>
      }

      <Modal
        opened={notFoundModalOpen}
        onClose={closeNotFoundModal}
        title={
          <Text fw={600} size="md">
            {language === 'ru' ? 'Я не нашел то, что искал' : "I didn't find what I was looking for"}
          </Text>
        }
        centered
        withCloseButton={true}
        radius="md"
        lockScroll={false}
      >
        {notFoundSubmitted ? (
          <Stack gap="md" align="center" py="md">
            <Text size="md" fw={500}>
              {language === 'ru' ? 'Спасибо!' : 'Thank you!'}
            </Text>
            <Button variant="light" onClick={closeNotFoundModal}>
              {language === 'ru' ? 'Закрыть' : 'Close'}
            </Button>
          </Stack>
        ) : (
          <Stack gap="md">
            <Text size="sm">
              {language === 'ru'
                ? 'Пожалуйста, укажите ссылку на произведение на любой платформе или название с годом выпуска. Это необязательно, но поможет сделать приложение лучше!'
                : 'Please share a link to the title on any platform or the title and release year. It is optional, but will help make the app better!'}
            </Text>
            <SegmentedControl
              value={notFoundCategory}
              onChange={setNotFoundCategory}
              data={[
                { label: language === 'ru' ? 'Фильм' : 'Movie', value: 'movie' },
                { label: language === 'ru' ? 'Книга' : 'Book', value: 'book' },
                { label: language === 'ru' ? 'Игра' : 'Game', value: 'videoGame' },
              ]}
              size="sm"
              radius="xl"
              fullWidth
            />
            <TextInput
              label={language === 'ru' ? 'Поисковый запрос' : 'Search query'}
              value={(searchTerm || (searchInputRef.current && searchInputRef.current.value) || '').trim()}
              readOnly
              radius="md"
              size="sm"
            />
            <Stack gap="xs">
              <TextInput
                label={language === 'ru' ? 'Ссылка на произведение' : 'Link to the title'}
                placeholder={'https://...'}
                value={notFoundLink}
                onChange={(e) => setNotFoundLink(e.target.value)}
                radius="md"
                size="sm"
              />
              <Text size="xs" c="dimmed" ta="center">
                {language === 'ru' ? 'или' : 'or'}
              </Text>
              <TextInput
                placeholder={language === 'ru' ? 'Название произведения и год выпуска' : 'Title and release year'}
                value={notFoundNameAndYear}
                onChange={(e) => setNotFoundNameAndYear(e.target.value)}
                radius="md"
                size="sm"
              />
            </Stack>
            <Button
              onClick={submitNotFoundHint}
              loading={notFoundSubmitting}
              disabled={notFoundSubmitting}
            >
              {language === 'ru' ? 'Отправить' : 'Submit'}
            </Button>
          </Stack>
        )}
      </Modal>

      {tagSearch && (
        <div className="flex flex-wrap justify-center items-center gap-2 my-2 px-2">
          <Text size="sm" c="dimmed">{language === 'ru' ? 'По тегам:' : 'By tags:'}</Text>
          {tagSearch.tags.map(t => (
            <Badge key={`${t.dimension}:${t.value}`} color={DIMENSION_COLORS[t.dimension] || 'gray'} variant="filled" size="lg" radius="sm"
              style={{ textTransform: 'none' }}
              rightSection={
                <span style={{ cursor: 'pointer', marginLeft: 4 }} aria-label="remove"
                  onClick={() => updateTagSearch({ ...tagSearch, tags: tagSearch.tags.filter(x => !(x.dimension === t.dimension && x.value === t.value)) })}>
                  ✕
                </span>
              }>
              {language === 'ru' && t.ru ? t.ru : t.value}
            </Badge>
          ))}
          {tagSearch.tags.length < 8 && (
            <TagPicker language={language} exclude={tagSearch.tags}
              onPick={(t) => updateTagSearch({ ...tagSearch, tags: [...tagSearch.tags, { dimension: t.dimension, value: t.value, ru: t.ru || '' }] })} />
          )}
          <SegmentedControl
            size="xs"
            radius="sm"
            value={tagSearch.family ? 'family' : 'exact'}
            onChange={(v) => updateTagSearch({ ...tagSearch, family: v === 'family' })}
            data={[
              { value: 'exact', label: language === 'ru' ? 'только эти теги' : 'these tags only' },
              { value: 'family', label: language === 'ru' ? 'все связанные' : 'all related' },
            ]}
          />
        </div>
      )}

      {
        searching && 
        <div className="flex justify-center">
          <DotSpinner />
        </div>
      }

      {activeTab === "users" && foundUsers && (
        <div className="mt-4 flex justify-center">
          <Paper shadow="xs" radius="md" className="w-full" style={{ maxWidth: 480, padding: 8 }}>
            {foundUsers.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                {language === 'ru' ? 'Пользователи не найдены' : 'No users found'}
              </Text>
            ) : (
              foundUsers.map((u) => (
                <Link key={u.id} to={`/users/${u.id}/likes`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div
                    className="flex items-center gap-3 p-2 rounded-md cursor-pointer"
                    style={{ transition: 'background 120ms' }}
                    onMouseOver={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Avatar src={u.avatarUrl} size="md" radius="xl" />
                    <Text size="sm" fw={500}>{u.userName}</Text>
                  </div>
                </Link>
              ))
            )}
          </Paper>
        </div>
      )}

      <div className="flex justify-center">
        <div id={activeTab} className="grid grid-cols-4 gap-3">
          {foundArtContainerByTab(activeTab) && foundArtContainerByTab(activeTab).map((foundArt, index) => (
            <div id={foundArt.pageUrl} className="hover:shadow-lg transition-shadow duration-200">
              <div>
                <div id={foundArt.pageUrl+"0"} className="flex justify-center">
                  <Link to={foundArt.sourceArt ? "/arts/"+foundArt.sourceArt.id : "/artByLink?link="+foundArt.pageUrl}>
                    <ArtThumbnail art={foundArt.sourceArt ? foundArt.sourceArt : {thumbnailUrl: foundArt.thumbUrl}} />
                  </Link>
                </div>
                <div className="flex justify-center items-center text-center w-full h-auto text-sm font-medium mt-2">
                  {( (language === "en" || !foundArt.ruName) ? foundArt.enName : foundArt.ruName) + formatYear(foundArt.year, language)}
                </div>
                {activeTab === "book" && foundArt.author && (
                  <div className="flex justify-center items-center text-center w-full h-auto text-xs mt-1" style={{ color: "var(--mantine-color-dimmed)" }}>
                    {foundArt.author}
                  </div>
                )}
                {tagSearch && foundArt.sourceArt && foundArt.sourceArt.searchTags && (
                  <div className="flex flex-wrap justify-center gap-1 mt-1">
                    {foundArt.sourceArt.searchTags.map(t => (
                      <Badge key={`${t.dimension}:${t.value}`} color={DIMENSION_COLORS[t.dimension] || 'gray'} variant="light" size="xs" radius="sm" style={{ textTransform: 'none' }}>
                        {language === 'ru' && t.ru ? t.ru : t.value}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex justify-center mt-1">
                {
                  foundArt.sourceArt ?
                    <PlatformRatings art={foundArt.sourceArt} />
                  :
                    foundArt.ratings &&
                      <PlatformRatings art={foundArt.ratings} />
                }
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {(trendingMovies || trendingBooks || trendingGames) && !(foundMovies || foundBooks || foundGames || foundUsers) && !searchTerm && !tagSearch && (
        <div className="mt-1">
          <div className="flex justify-center items-center gap-1 mb-2">
            <SegmentedControl
              value={trendingCategory}
              onChange={setTrendingCategory}
              data={categoryOrder.map(c => ({ label: categoryLabels[c], value: c }))}
              size="sm"
              withItemsBorders={false}
              styles={{ root: { border: 'none' }, control: { border: 'none' } }}
            />
            <Tooltip
              label={
                trendingCategory === 'movie'
                  ? (language === "ru"
                    ? "Новые фильмы. Те, которые сейчас активнее всего получают оценки на IMDb и Кинопоиске — выше."
                    : "New movies. Those currently most actively reviewed on IMDb are at the top.")
                  : trendingCategory === 'book'
                  ? (language === "ru" ? "Новые книги." : "New books.")
                  : (language === "ru" ? "Новые игры. С более высоким рейтингом - выше." : "New games. Higher-rated ones are on top.")
              }
              multiline
              w={250}
              withArrow
              events={{ hover: true, focus: true, touch: true }}
            >
              <ActionIcon variant="subtle" size="sm" color="dimmed">
                <IconInfoCircle size={18} />
              </ActionIcon>
            </Tooltip>
          </div>
          <div className="flex justify-center text-xs text-center mb-2" style={{ color: "var(--mantine-color-dimmed)" }}>
            {formatUpdatedAgo(currentTrendingUpdatedTime)}
          </div>
          {(() => {
            const catItems = currentTrending || [];
            const freq = new Map();
            for (const it of catItems) {
              for (const g of (it.genres || [])) {
                if (!g) continue;
                freq.set(g, (freq.get(g) || 0) + 1);
              }
            }
            const genreList = Array.from(freq.entries())
              .sort((a, b) => b[1] - a[1])
              .map(([g]) => g);
            if (genreList.length === 0) return null;
            const allLabel = language === "ru" ? "Все" : "All";
            const pillStyle = (active) => ({
              padding: '4px 10px',
              borderRadius: '9999px',
              border: '1px solid var(--app-border)',
              backgroundColor: active ? 'var(--mantine-color-blue-filled)' : 'var(--app-surface)',
              color: active ? 'var(--mantine-color-white)' : 'var(--mantine-color-text)',
              fontSize: '12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              flexShrink: 0,
            });
            return (
              <div
                ref={(el) => {
                  if (!el || el.__wheelHorizontalAttached) return;
                  el.__wheelHorizontalAttached = true;
                  el.addEventListener('wheel', (e) => {
                    if (e.deltaX !== 0) return;
                    if (el.scrollWidth <= el.clientWidth) return;
                    el.scrollLeft += e.deltaY;
                    e.preventDefault();
                  }, { passive: false });
                }}
                className="flex gap-1.5 mb-2 px-1 pb-1 overflow-x-auto no-scrollbar"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <div
                  style={pillStyle(selectedGenre === null)}
                  onClick={() => setSelectedGenre(null)}
                >
                  {allLabel}
                </div>
                {genreList.map((g) => (
                  <div
                    key={g}
                    style={pillStyle(selectedGenre === g)}
                    onClick={() => setSelectedGenre(selectedGenre === g ? null : g)}
                  >
                    {localizeGenre(g, language)}
                  </div>
                ))}
              </div>
            );
          })()}
          <div
            onTouchStart={(e) => {
              swipeTouchXRef.current = e.touches[0].clientX;
              swipeTouchYRef.current = e.touches[0].clientY;
              swipeActiveRef.current = false;
            }}
            onTouchMove={(e) => {
              const dx = e.touches[0].clientX - swipeTouchXRef.current;
              const dy = e.touches[0].clientY - swipeTouchYRef.current;
              if (!swipeActiveRef.current && Math.abs(dx) > 20 && Math.abs(dx) > Math.abs(dy) * 1.2) {
                swipeActiveRef.current = true;
              }
            }}
            onTouchEnd={(e) => {
              if (!swipeActiveRef.current) return;
              e.preventDefault();
              const dx = e.changedTouches[0].clientX - swipeTouchXRef.current;
              const idx = categoryOrder.indexOf(trendingCategoryRef.current);
              if (dx < -40 && idx < categoryOrder.length - 1) {
                setTrendingCategory(categoryOrder[idx + 1]);
              }
              if (dx > 40 && idx > 0) {
                setTrendingCategory(categoryOrder[idx - 1]);
              }
            }}
            style={{ minHeight: 100, touchAction: 'pan-y' }}
          >
            {(() => {
              // null = still loading; [] = loaded, empty; otherwise a list.
              if (currentTrending === null) {
                return (
                  <div className="flex justify-center items-center py-12" style={{ color: "var(--mantine-color-dimmed)" }}>
                    {language === "ru" ? "Загрузка..." : "Loading..."}
                  </div>
                );
              }
              let items = currentTrending;
              if (selectedGenre) {
                items = items.filter((it) => (it.genres || []).includes(selectedGenre));
              }
              if (items.length === 0) {
                return (
                  <div className="flex justify-center items-center py-12" style={{ color: "var(--mantine-color-dimmed)" }}>
                    {language === "ru" ? "Скоро появятся" : "Coming soon"}
                  </div>
                );
              }
              const isGames = trendingCategory === 'videoGame';
              const cols = window.innerWidth >= 640 ? (isGames ? 5 : 6) : (isGames ? 3 : 4);
              const gridClass = isGames
                ? "grid grid-cols-3 sm:grid-cols-5 gap-1.5 mb-1.5"
                : "grid grid-cols-4 sm:grid-cols-6 gap-1.5 mb-1.5";
              // For games only: reorder so consecutive `cols`-item groups
              // share a similar aspect ratio, keeping sort order as much
              // as possible. Non-games and games without dimension data
              // fall through unchanged.
              if (isGames) {
                items = packRowsByAspect(items, cols);
              }
              const elements = [];
              for (let i = 0; i < items.length; i += cols) {
                const rowItems = items.slice(i, i + cols);
                const selectedInRow = selectedTrendingId && rowItems.some(a => a.id === selectedTrendingId);
                const selectedArtLite = selectedInRow ? rowItems.find(a => a.id === selectedTrendingId) : null;
                const detail = selectedTrendingId ? trendingDetails[selectedTrendingId] : null;

                elements.push(
                  <div key={`row-${i}`} className={gridClass}>
                    {rowItems.map((art) => {
                      const localized = localizeArt(art, language);
                      return (
                        <div key={art.id} className="cursor-pointer relative group" onClick={() => handleSelectTrending(art.id)}>
                          <ArtThumbnail art={art} />
                          <TrendingRatingsOverlay art={art} />
                          {currentTrendingMostRecentIDs && currentTrendingMostRecentIDs.has(art.id) && (
                            <div style={{ position: 'absolute', top: 4, left: 4 }}>
                              <Badge size="xs" variant="filled" color="green" style={{ fontSize: 9, padding: '0 4px', lineHeight: '16px' }}>
                                {language === 'ru' ? 'Новое' : 'New'}
                              </Badge>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );

                if (selectedInRow && selectedArtLite) {
                  const desc = detail || {};
                  const description = language === 'ru'
                    ? (desc.ruDescription || desc.description)
                    : (desc.description || desc.ruDescription);
                  const genres = selectedArtLite.genres || [];
                  const author = selectedArtLite.author || '';
                  elements.push(
                    <Paper key={`detail-${i}`} ref={scrollDetailIntoView} shadow="xs" radius="md" className="mb-2 p-1.5" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
                      <div className="flex gap-3">
                        <Link to={`/arts/${selectedArtLite.id}`}>
                          <img src={selectedArtLite.thumbnailUrl} alt="" className="w-[83px] h-auto object-cover rounded-md" />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={`/arts/${selectedArtLite.id}`}>
                            <Text size="sm" fw={600}>{localizeArt(selectedArtLite, language).name + formatYear(selectedArtLite.year, language)}</Text>
                          </Link>
                          {author && (
                            <Text size="xs" c="dimmed" className="mt-0.5">
                              {selectedArtLite.category === 1
                                ? (language === 'ru' ? 'Режиссёр' : 'Director')
                                : selectedArtLite.category === 2
                                  ? (language === 'ru' ? 'Разработчик' : 'Developer')
                                  : (language === 'ru' ? 'Автор' : 'Author')
                              }: {author}
                            </Text>
                          )}
                          {genres.length > 0 && (
                            <Text size="xs" c="dimmed" className="mt-0.5">
                              {localizeGenres(genres, language).join(', ')}
                            </Text>
                          )}
                          <div className="mt-1">
                            <PlatformRatings art={selectedArtLite} center={false} showVotes={true} />
                          </div>
                          {description ? (
                            <Text size="xs" className="mt-1" style={{ lineHeight: 1.4 }}>
                              {description}
                            </Text>
                          ) : detail && (
                            <Text size="xs" c="dimmed" className="mt-1" style={{ fontStyle: 'italic' }}>
                              {language === 'ru' ? 'Описание отсутствует' : 'No description available'}
                            </Text>
                          )}
                        </div>
                      </div>
                    </Paper>
                  );
                }
              }
              return elements;
            })()}
          </div>
        </div>
      )}

    </div>
  );
}

export default AddEvent;
